import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ServingGeneratorConfig } from '../../../components/ServingGeneratorConfig';
import { useDebugMode } from '../../../hooks/useDebugMode';
import { getServingGeneratorConfig, listExperiments } from '../../../services/EvalService';
import { useUnlimitedContext } from '../contexts/UnlimitedContext';

/**
 * Admin debug-mode panel on the workshop: what generator-configuration this
 * environment serves you right now, and the pairwise-compare experiment picker.
 * Renders nothing unless debug mode is on (which is admin-only).
 */
export const DebugComparePanel = () => {
  const [debugMode] = useDebugMode();
  const { compare, template } = useUnlimitedContext();
  const [serving, setServing] = useState(null);
  const [servingError, setServingError] = useState(null);
  const [experiments, setExperiments] = useState([]);

  useEffect(() => {
    if (!debugMode) return;
    getServingGeneratorConfig()
      .then((res) => setServing(res.data))
      .catch(() => setServingError('Could not load the served config.'));
    listExperiments()
      .then((res) => {
        const active = (res.data.experiments || []).filter((e) => !e.archived);
        setExperiments(active);
        // A remembered pick that has since been archived or deleted must not keep
        // turning creates into comparisons behind a picker that no longer shows it.
        if (compare.experimentId && !active.some((e) => e.id === compare.experimentId)) {
          compare.setExperimentId(null);
        }
      })
      .catch(() => {
        // Can't confirm the remembered pick, and the picker will show "Off" — so make
        // it off rather than run a comparison nobody can see was selected.
        setExperiments([]);
        compare.setExperimentId(null);
      });
    // Only on enabling debug mode, not on every pick.
  }, [debugMode]);

  if (!debugMode) return null;

  return (
    <div className='mt-4 rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-4 text-sm text-gray-700'>
      <div className='flex items-center justify-between mb-2'>
        <span className='text-xs font-semibold uppercase tracking-wide text-amber-700'>
          Debug mode
        </span>
        <Link to='/eval?tab=experiments' className='text-xs text-amber-700 hover:underline'>
          Manage experiments
        </Link>
      </div>

      <ServingGeneratorConfig serving={serving} error={servingError} />

      <div className='mt-3 flex items-center gap-2 border-t border-amber-200 pt-3'>
        <label htmlFor='debug-compare-experiment' className='font-medium'>
          Pairwise compare
        </label>
        <select
          id='debug-compare-experiment'
          value={compare.experimentId || ''}
          onChange={(e) => compare.setExperimentId(e.target.value || null)}
          className='rounded-md border-gray-300 py-1 text-sm focus:border-amber-500 focus:ring-amber-500'>
          <option value=''>Off — normal create</option>
          {experiments.map((experiment) => (
            <option key={experiment.id} value={experiment.id}>
              {experiment.name}
            </option>
          ))}
        </select>
      </div>
      {compare.experimentId && template.selectedTemplate && (
        <p className='mt-2 text-xs text-amber-700'>
          Comparisons don&apos;t apply templates, so this create won&apos;t run as one.
        </p>
      )}
    </div>
  );
};
